// The pages of games in the browser: the ranking (/), the tags index, a
// tag's or a company's paged list, the search page and its form (wired up
// as this is imported), and the not-found page. What each says is
// list-pages.ts's and meta.ts's; a page the Worker wrote stays on screen
// while anything it couldn't embed loads.

import type { AppsResponse, MoversResponse, SearchResponse, TagResponse } from "../../shared/api.ts";
import { TAG_PAGE_LIST } from "../../shared/site.ts";
import { errorMessage, isNotFound } from "./api-client.ts";
import { app, element, eventTarget } from "./dom.ts";
import { setMeta } from "./head.ts";
import {
  listBody,
  listIntro,
  searchCount,
  searchPage,
  searchResults,
  tagsBody,
  tagsPage
} from "./list-pages.ts";
import { pageHref } from "./markup.ts";
import { listMeta, notFoundMeta, notFoundPage, type PageMeta, searchMeta, tagsMeta } from "./meta.ts";
import {
  listPage,
  listPath,
  MOVERS_PATH,
  moversPath,
  searchFormQuery,
  searchHref,
  searchPath,
  searchQuery,
  tagApiPath,
  TAGS_PREVIEW_GAMES
} from "./routes.ts";
import { backLink, navigate, setBackTo, setListHref } from "./router.ts";
import { api, isCurrent, prerendered } from "./state.ts";
import { escapeHtml } from "./web.ts";

export async function renderList(token: number): Promise<void> {
  const page = listPage(location.search);

  setListHref(pageHref(page));
  setMeta(listMeta(page));

  // A list the Worker wrote stays while anything it couldn't embed
  // (the tag cards, if that call failed) loads.
  if (!prerendered(token)) {
    app.innerHTML = `${listIntro()}<div class="panel status">Loading…</div>`;
  }

  try {
    // Page 0 leads with the tag cards, fetched alongside the page so both
    // appear at once rather than the list jumping down. It's an extra, so if
    // it fails the list still shows, without it. A page the Worker wrote
    // arrives with both responses in it, so neither is asked for again.
    const [list, movers] = await Promise.all([
      api<AppsResponse>(listPath(page)),
      page === 0 ? api<MoversResponse>(MOVERS_PATH).catch(() => null) : null
    ]);
    if (!isCurrent(token)) return;

    setMeta(listMeta(page, list));
    app.innerHTML = listIntro() + listBody(page, list, movers);
  } catch (err) {
    if (isCurrent(token)) {
      app.innerHTML = `${listIntro()}<div class="panel status">${errorMessage(err)}</div>`;
    }
  }
}

// The tags index (list-pages.ts's tagsPage): the static tags.html arrives with it
// drawn, as links; each tag's top games then fill in as cards (tagsBody). It's
// an extra, so if they fail to load the links stay.
export async function renderTags(token: number): Promise<void> {
  setBackTo("All tags");
  setMeta(tagsMeta);
  if (!prerendered(token)) app.innerHTML = tagsPage;

  try {
    // The movers tags come in one request; the others' cards (busy_hours) one
    // each, into the same shape.
    // A copy: the response is the cache's, shared with every later reader.
    const tags = { ...(await api<MoversResponse>(moversPath(TAGS_PREVIEW_GAMES))).tags };
    const rest = TAG_PAGE_LIST.filter(({ tag }) => !tags[tag]);
    // One request failing leaves its cards as links, not every card.
    const lists = await Promise.allSettled(
      rest.map(({ tag, value }) => api<TagResponse>(tagApiPath(tag, value, 0, TAGS_PREVIEW_GAMES)))
    );
    if (!isCurrent(token)) return;

    rest.forEach(({ tag, value }, i) => {
      const result = lists[i];
      if (result?.status === "fulfilled") (tags[tag] ??= {})[value] = result.value.apps;
    });
    app.innerHTML = tagsBody(tags);
  } catch {}
}

// A paged list's page (a tag's or a company's; see renderList), from its API
// response (`apiPath(page)`), with head tags `meta(page, list)` and content
// `body(page, list)` (each drawing a loading page without `list`): what the
// Worker wrote stays while it loads. A list the API doesn't know is "Page not
// found". Once loaded, game pages lead back to it, as `label(list)`.
export async function renderPagedPage<L>(
  token: number,
  {
    apiPath,
    meta,
    body,
    label
  }: {
    apiPath: (page: number) => string;
    meta: (page: number, list?: L) => PageMeta;
    body: (page: number, list?: L) => string;
    label: (list: L) => string;
  }
): Promise<void> {
  const page = listPage(location.search);

  setMeta(meta(page));
  if (!prerendered(token)) app.innerHTML = body(page);

  try {
    const list = await api<L>(apiPath(page));
    if (!isCurrent(token)) return;

    setMeta(meta(page, list));
    app.innerHTML = body(page, list);
    setBackTo(label(list));
  } catch (err) {
    if (!isCurrent(token)) return;
    if (isNotFound(err)) return renderNotFound();
    // A page the Worker wrote keeps its rows; one still loading says so.
    const status = app.querySelector(".status");
    if (status) status.textContent = errorMessage(err);
  }
}

// The search page (list-pages.ts's searchPage), which the Worker may have
// written. When only its query changed (a box ticked, a name entered, back
// or forward), the form stays as it is, set to the query, so focus and
// scroll aren't lost; the last results stay too, dimmed, until the new ones
// load, so the page doesn't shrink under the reader.
export async function renderSearch(token: number): Promise<void> {
  const query = searchQuery(location.search);
  const path = searchPath(query, query.page);
  const form = app.querySelector<HTMLFormElement>("form.search-form");

  // The results, done loading, and what screen readers hear of them: the
  // count, not every row.
  const show = (html: string, said: string): void => {
    const results = element(".search-results", HTMLElement, app);
    results.innerHTML = html;
    results.removeAttribute("aria-busy");
    element(".search-status", HTMLElement, app).textContent = said;
  };

  setMeta(searchMeta(query));
  if (path) setBackTo("Search results");
  if (form) {
    element("input[name=q]", HTMLInputElement, form).value = query.q;
    for (const box of form.querySelectorAll<HTMLInputElement>("input[type=checkbox]")) {
      box.checked = query.tags[box.name]?.includes(box.value) ?? false;
    }
    if (path) element(".search-results", HTMLElement, app).setAttribute("aria-busy", "true");
    else show(searchResults(query, undefined), "");
  } else {
    app.innerHTML = searchPage(query, backLink(), undefined);
  }
  if (!path) return;

  try {
    const list = await api<SearchResponse>(path);
    if (isCurrent(token)) show(searchResults(query, list), searchCount(list));
  } catch (err) {
    const message = errorMessage(err);
    if (isCurrent(token)) show(`<div class="panel status">${escapeHtml(message)}</div>`, message);
  }
}

// The search page's form: Enter or the button searches, and so does
// ticking a box, from page 0, once the boxes have been still for
// TICK_WAIT_MS, so a few ticked in a row are one search. Each search is a
// history entry, so back undoes it.
const TICK_WAIT_MS = 400;
let tickTimer: ReturnType<typeof setTimeout> | undefined;

function searchFrom(form: HTMLFormElement): void {
  clearTimeout(tickTimer);
  navigate(searchHref(searchFormQuery(new FormData(form))), { scroll: false });
}

app.addEventListener("submit", (event) => {
  if (!(event.target instanceof HTMLFormElement) || !event.target.matches("form.search-form")) return;
  event.preventDefault();
  searchFrom(event.target);
});

app.addEventListener("change", (event) => {
  const box = event.target;
  const form = eventTarget<HTMLFormElement>(event, "form.search-form");
  if (form && box instanceof HTMLInputElement && box.type === "checkbox") {
    clearTimeout(tickTimer);
    tickTimer = setTimeout(() => form.isConnected && searchFrom(form), TICK_WAIT_MS);
  }
});

export function renderNotFound(): void {
  setMeta(notFoundMeta(location.pathname));
  app.innerHTML = notFoundPage;
}
